import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import BuilderLoginScreen from "./BuilderLoginScreen";
import BuilderForgotScreen from "./BuilderForgotScreen";
import BuilderChangePasswordScreen from "./BuilderChangePasswordScreen";
import BuilderSignupScreen from "./BuilderSignupScreen";
import Developer from "../models/Developer";
import BuilderEmailVerificationView from "./BuilderEmailVerificationView";

type _BuilderLoginOnLoginUser = (developer: Developer) => void;

export interface BuilderLoginProps extends BaseUIProps {
  key?: string;
  onLoginUser: _BuilderLoginOnLoginUser;
}

class _BuilderLoginState extends ObservableComponent<BuilderLoginProps> {
  path: string = "login";
  isNewUser: boolean = false;
  email: string = "";
  generatedToken: string = "";
  user: Developer = null;
  public constructor(props: BuilderLoginProps) {
    super(props);

    this.initState();
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["email", "generatedToken", "isNewUser", "path", "user"],
      this.rebuild
    );
  }
  public setPath(val: string): void {
    let isValChanged: boolean = this.path !== val;

    if (!isValChanged) {
      return;
    }

    this.path = val;

    this.fire("path", this);
  }
  public setIsNewUser(val: boolean): void {
    let isValChanged: boolean = this.isNewUser !== val;

    if (!isValChanged) {
      return;
    }

    this.isNewUser = val;

    this.fire("isNewUser", this);
  }
  public setEmail(val: string): void {
    let isValChanged: boolean = this.email !== val;

    if (!isValChanged) {
      return;
    }

    this.email = val;

    this.fire("email", this);
  }
  public setGeneratedToken(val: string): void {
    let isValChanged: boolean = this.generatedToken !== val;

    if (!isValChanged) {
      return;
    }

    this.generatedToken = val;

    this.fire("generatedToken", this);
  }
  public setUser(val: Developer): void {
    let isValChanged: boolean = this.user !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("user", this.user, val);

    this.user = val;

    this.fire("user", this);
  }
  public _beforeLoginSwitch(switchValue: string): ReactNode {
    switch (switchValue) {
      case "login": {
        return BuilderLoginScreen({
          onSignup: () => {
            this.loginRefHandler();
          },
          onForgot: () => {
            this.onForgotScreen();
          },
          onLoginUser: (developer) => {
            this.onLoginSuccess(developer);
          },
          className: "xed7 hc h",
        });
      }

      case "signup": {
        return BuilderSignupScreen({
          onLogin: () => {
            this.signupRefHandler();
          },
          onEmailVerification: (email, generatedToken) => {
            this.emailVerifyRefHandler(email, generatedToken);
          },
          className: "x425 hc h",
        });
      }

      case "forgot": {
        return BuilderForgotScreen({
          moveToLogin: () => {
            this.onMoveToLoginHandler();
          },
          onChangePassword: (developer) => {
            this.onChangePasswordHandler(developer);
          },
          className: "xc5 hc h",
        });
      }

      case "emailVerify": {
        return BuilderEmailVerificationView({
          email: this.email,
          generatedToken: this.generatedToken,
          onLoginClick: () => {
            this.onEmailVerificationRef();
          },
          onVerificationSuccess: (user) => {
            this.onEmailVerificationRef1(user);
          },
          className: "xf5 hc h",
        });
      }

      case "changePassword": {
        return BuilderChangePasswordScreen({
          user: this.user,
          isNewUser: this.isNewUser,
          onLoginUser: (user) => {
            this.onChangePasswordSuccess(user);
          },
          className: "x73 hc",
        });
      }
      default: {
        return ui.Container({ className: "x03 hc" });
      }
    }
  }
  public render(): ReactNode {
    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      children: [this._beforeLoginSwitch(this.path)],
      className: ui.join(this.props.className, "BuilderLogin hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public loginRefHandler = (): void => {
    this.setPath("signup");
  };
  public onForgotScreen = (): void => {
    this.setPath("forgot");
  };
  public signupRefHandler = (): void => {
    this.setPath("login");
  };
  public emailVerifyRefHandler = (
    email: string,
    generatedToken: string
  ): void => {
    this.setPath("emailVerify");

    this.setEmail(email);

    this.setGeneratedToken(generatedToken);
  };
  public onEmailVerificationRef = (): void => {
    this.setPath("login");
  };
  public onEmailVerificationRef1 = (user: Developer): void => {
    this.setUser(user);

    this.setIsNewUser(true);

    this.setPath("changePassword");
  };
  public onChangePasswordHandler = (developer: Developer): void => {
    this.setUser(developer);

    this.setPath("changePassword");
  };
  public onMoveToLoginHandler = (): void => {
    this.setPath("login");
  };
  public onLoginSuccess = (developer: Developer): void => {
    this.setUser(developer);

    this.onLoginUser(developer);
  };
  public onChangePasswordSuccess = (user: Developer): void => {
    this.setUser(user);

    this.onLoginUser(user);
  };
  public get onLoginUser(): _BuilderLoginOnLoginUser {
    return this.props.onLoginUser;
  }
}
export default function BuilderLogin(props: BuilderLoginProps) {
  return React.createElement(_BuilderLoginState, props);
}
